import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/AuthContext";
import { useSettings } from "../lib/SettingsContext";
import { db } from "../lib/firebase";
import { collection, getDocs, query, where, doc, updateDoc } from "firebase/firestore";
import { getBookingStatusInfo } from "../lib/constants";
import { Sparkles, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Spinner, PageLoader } from "../components/Spinner";

export default function Dashboard() {
  const { user, profile } = useAuth();
  const { bookingEnabled } = useSettings();
  const [bookings, setBookings] = useState([]);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);

  const loadData = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const bq = query(
        collection(db, "bookings"),
        where("uid", "==", user.uid)
      );
      const bsnap = await getDocs(bq);
      const list = bsnap.docs.map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.date + b.timeSlot).localeCompare(a.date + a.timeSlot));
      setBookings(list);

      const sq = query(
        collection(db, "subscriptions"),
        where("uid", "==", user.uid),
        where("status", "in", ["active", "pending_approval"])
      );
      const ssnap = await getDocs(sq);
      if (!ssnap.empty) setSub({ id: ssnap.docs[0].id, ...ssnap.docs[0].data() });
      else setSub(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [user]);

  const cancelBooking = async (id) => {
    setCancellingId(id);
    try {
      await updateDoc(doc(db, "bookings", id), { status: "cancelled" });
      toast.success("Booking cancelled");
      await loadData();
    } catch (err) {
      toast.error("Failed: " + err.message);
    } finally {
      setCancellingId(null);
    }
  };

  const upcomingList = [...bookings]
    .filter((b) => b.status === "pending" || b.status === "confirmed")
    .sort((a, b) => (a.date + a.timeSlot).localeCompare(b.date + b.timeSlot));

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header row */}
          <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
            <div>
              <div className="font-mono-accent text-xs text-[#002FA7] mb-3">[ MY DASHBOARD ]</div>
              <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">
                Hi, {profile?.name?.split(" ")[0] || "there"}.
              </h1>
            </div>
            {bookingEnabled ? (
              <Link to="/book" data-testid="dashboard-book-cta">
                <Button className="bg-[#002FA7] hover:bg-[#001B6B] rounded-none font-bold uppercase tracking-wider px-6 h-12 text-xs">
                  <Plus size={16} className="mr-2" /> New Booking
                </Button>
              </Link>
            ) : (
              <Button
                data-testid="dashboard-book-cta"
                disabled
                className="bg-[#D4D4D8] text-[#525252] rounded-none font-bold uppercase tracking-wider px-6 h-12 text-xs cursor-not-allowed"
              >
                Coming Soon
              </Button>
            )}
          </div>

          {/* Top cards */}
          <div className="grid lg:grid-cols-3 gap-px bg-[#D4D4D8] border border-[#D4D4D8] mb-12">
            <div className="bg-white p-8 lg:col-span-2">
              <div className="font-mono-accent text-xs text-[#525252] mb-4">
                UPCOMING WASHES {upcomingList.length > 0 && `(${upcomingList.length})`}
              </div>
              {loading ? (
                <PageLoader label="Loading…" />
              ) : upcomingList.length === 0 ? (
                <div>
                  <div className="font-display font-bold text-xl text-[#525252]">No upcoming wash</div>
                  <p className="text-sm text-[#525252] mt-2">Book your first wash to keep your ride spotless.</p>
                </div>
              ) : (
                <div className="space-y-5">
                  {upcomingList.map((b, i) => (
                    <div key={b.id} className={`flex items-start justify-between gap-4 ${i > 0 ? "pt-5 border-t border-[#D4D4D8]" : ""}`}>
                      <div>
                        <div className="font-display font-black text-2xl leading-tight">
                          {new Date(b.date).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}
                          <span className="font-mono text-lg ml-2 font-normal">{b.timeSlot}</span>
                        </div>
                        <div className="text-[#525252] text-sm mt-1">{b.serviceLabel} · {b.vehicleLabel}</div>
                        <div className="mt-2 flex gap-2 flex-wrap">
                          <span className={`text-xs font-mono-accent px-3 py-1 ${getBookingStatusInfo(b.status).color}`}>
                            {getBookingStatusInfo(b.status).label}
                          </span>
                          <span className="text-xs font-mono-accent px-3 py-1 bg-[#F4F4F5] text-[#525252]">
                            {b.useSubscription ? "FREE" : `₹${b.price}`}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => cancelBooking(b.id)}
                        disabled={cancellingId === b.id}
                        className="shrink-0 font-mono-accent text-[10px] text-[#FF2A2A] hover:bg-[#FF2A2A] hover:text-white border border-[#FF2A2A] px-3 py-1.5 transition-colors disabled:opacity-50"
                      >
                        {cancellingId === b.id ? <Spinner /> : "CANCEL"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-[#0A0A0A] text-white p-8 relative">
              <div className="font-mono-accent text-xs text-[#FFC000] mb-3">SUBSCRIPTION</div>
              {sub ? (
                <>
                  <div className="font-display font-black text-2xl mb-2">{sub.planName}</div>
                  {sub.status === "pending_approval" ? (
                    <>
                      <div className="font-mono-accent text-xs text-[#FFC000] mb-3">● AWAITING APPROVAL</div>
                      <div className="text-white/60 text-sm mb-4">
                        Admin will activate your plan shortly.
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="font-mono-accent text-xs text-[#00D05A] mb-4">● ACTIVE</div>
                      <div className="flex items-end gap-3 mb-2">
                        <div className="font-display font-black text-6xl leading-none text-[#FFC000]">
                          {sub.washesRemaining === 999 ? "∞" : sub.washesRemaining ?? 0}
                        </div>
                        <div className="pb-2">
                          <div className="font-mono-accent text-[10px] text-white/60">WASHES</div>
                          <div className="font-mono-accent text-[10px] text-white/60">LEFT</div>
                        </div>
                      </div>
                      {sub.washesRemaining !== 999 && (
                        <div className="text-white/60 text-xs mb-4 font-mono-accent">
                          OF {sub.washesPerMonth || sub.washCount || 0} TOTAL
                        </div>
                      )}
                    </>
                  )}
                  <div className="text-[10px] text-white/40 font-mono-accent mb-1">
                    STARTED {sub.startDate || "—"}
                  </div>
                  <div className="text-[10px] text-white/40 font-mono-accent">
                    {sub.status === "pending_approval" ? "EXPIRES" : "RENEWS"} {sub.endDate || "—"}
                  </div>
                  <Link to="/subscriptions">
                    <span className="inline-block mt-4 font-mono-accent text-xs text-[#FFC000] hover:underline">
                      MANAGE PLAN →
                    </span>
                  </Link>
                </>
              ) : (
                <>
                  <div className="font-display font-bold text-xl mb-3">Not subscribed</div>
                  <p className="text-sm text-white/60 mb-4">
                    Save up to 40% with our monthly packages.
                  </p>
                  <Link to="/subscriptions">
                    <span className="inline-flex items-center gap-1 font-mono-accent text-xs bg-[#FFC000] text-black px-3 py-2 hover:bg-white">
                      VIEW PLANS →
                    </span>
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Bookings table */}
          <div>
            <div className="font-mono-accent text-xs text-[#002FA7] mb-3">[ BOOKING HISTORY ]</div>
            <h2 className="font-display font-black text-3xl mb-8 tracking-tighter">All bookings</h2>

            {loading && <PageLoader label="Loading your bookings…" />}
            {!loading && bookings.length === 0 && (
              <div className="border border-[#D4D4D8] p-12 text-center">
                <Sparkles size={32} className="mx-auto text-[#525252] mb-4" strokeWidth={1.5} />
                <p className="text-[#525252]">No bookings yet.</p>
                {bookingEnabled ? (
                  <Link to="/book">
                    <Button className="mt-4 bg-[#002FA7] hover:bg-[#001B6B] rounded-none font-bold uppercase text-xs">
                      Book Now
                    </Button>
                  </Link>
                ) : (
                  <Button disabled className="mt-4 bg-[#D4D4D8] text-[#525252] rounded-none font-bold uppercase text-xs cursor-not-allowed">
                    Coming Soon
                  </Button>
                )}
              </div>
            )}
            {!loading && bookings.length > 0 && (
              <div className="border border-[#D4D4D8]">
                {bookings.map((b) => (
                  <div
                    key={b.id}
                    data-testid={`booking-row-${b.id}`}
                    className="grid grid-cols-12 gap-4 px-6 py-5 border-b border-[#D4D4D8] last:border-b-0 items-center text-sm"
                  >
                    <div className="col-span-12 md:col-span-3 font-mono">
                      <div className="font-bold">{b.date}</div>
                      <div className="text-[#525252]">{b.timeSlot}</div>
                      {b.createdAt?.toDate && (
                        <div className="text-[10px] text-[#525252]/70 font-mono-accent mt-1">
                          BOOKED {b.createdAt.toDate().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                        </div>
                      )}
                    </div>
                    <div className="col-span-6 md:col-span-3">
                      <div className="font-bold">{b.serviceLabel}</div>
                      <div className="text-[#525252] text-xs">{b.vehicleLabel}</div>
                      {b.useSubscription && (
                        <div className="text-[10px] font-mono-accent text-[#00D05A] mt-1">● PACKAGE</div>
                      )}
                    </div>
                    <div className="col-span-3 md:col-span-2 font-display font-bold">
                      {b.useSubscription ? <span className="text-[#00D05A] text-base">FREE</span> : `₹${b.price}`}
                    </div>
                    <div className="col-span-3 md:col-span-2">
                      <span className={`text-xs font-mono-accent px-2 py-1 ${getBookingStatusInfo(b.status).color}`}>
                        {getBookingStatusInfo(b.status).label}
                      </span>
                    </div>
                    <div className="col-span-12 md:col-span-2 flex md:justify-end">
                      {(b.status === "pending" || b.status === "confirmed") && (
                        <Button
                          data-testid={`cancel-booking-${b.id}`}
                          onClick={() => cancelBooking(b.id)}
                          disabled={cancellingId === b.id}
                          variant="ghost"
                          className="rounded-none text-xs text-[#FF2A2A] hover:bg-[#FF2A2A] hover:text-white font-mono-accent"
                        >
                          {cancellingId === b.id ? <Spinner /> : <><X size={14} className="mr-1" /> Cancel</>}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
